// The setup wizard's rail summary: what this cluster will be once launched.
//
// Lived inside WizardShell until the shell picked up a second caller. It is a
// property of first-run onboarding, not of the layout, so it lives beside the
// steps that fill it in.

import { Badge } from "@veil/ui/components/badge";
import {
	WizardSummaryList,
	WizardSummaryPending,
	WizardSummaryValue,
} from "@/components/wizard/wizard-summary";

export interface EnvironmentSummaryValue {
	hostname: string | null;
	/** Human TLS label, e.g. "Automatic HTTPS". */
	tls: string | null;
	workers: number | null;
	/** IP-only evaluation opt-out chosen on the cluster step. */
	evaluation?: boolean;
	/** When set, the console moves to this URL at launch (the wizard is being
	 * driven from a different origin, e.g. the raw IP). Shown as a rail note. */
	movesTo?: string | null;
}

export function EnvironmentSummary({
	summary,
}: {
	summary: EnvironmentSummaryValue;
}) {
	return (
		<WizardSummaryList
			heading="Environment"
			rows={[
				{
					label: "Hostname",
					value: summary.evaluation ? (
						<Badge variant="outline" className="font-normal">
							Evaluation (IP only)
						</Badge>
					) : summary.hostname ? (
						<WizardSummaryValue>{summary.hostname}</WizardSummaryValue>
					) : (
						<WizardSummaryPending />
					),
				},
				{
					label: "TLS",
					value: summary.tls ? (
						<span className="text-foreground">{summary.tls}</span>
					) : (
						<WizardSummaryPending />
					),
				},
				{
					label: "Workers",
					value:
						summary.workers === null ? (
							<WizardSummaryPending />
						) : (
							<span className="tabular-nums text-foreground">
								{summary.workers}
							</span>
						),
				},
			]}
		>
			{summary.movesTo && (
				<p className="mt-3 rounded-md border border-border/60 bg-muted/40 px-2.5 py-2 text-[0.7rem] leading-relaxed text-muted-foreground">
					At launch the console moves to{" "}
					<span className="font-mono text-foreground">{summary.movesTo}</span> —
					you'll be redirected automatically.
				</p>
			)}
		</WizardSummaryList>
	);
}
